import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  RadioGroup,
  RadioGroupItem,
} from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { classNames, fetcher, OptionType } from '@commenda/utils';
import { normalizeSSNOrITIN } from '@commenda/utils/normalizeSSNorITIN';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import Required from '../FormElements/Required';
import {
  ResponsiblePartyInfoFormFields,
  responsiblePartyInfoSchema,
} from './schema';

const USResponsiblePartyForm = ({
  director,
  companyDataMutate,
  setOpen,
  isAddingDirector,
  setIsAddingDirector,
  companyId,
  corporationId,
  token,
  setNotification,
  onErrorResolve,
  selectedDirectorIds,
  setSelectedDirectorIds,
  modalConfig,
  workRequestData,
}: {
  director?: Partial<DirectorBase>;
  companyDataMutate: KeyedMutator<unknown>;
  isAddingDirector: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAddingDirector: React.Dispatch<React.SetStateAction<boolean>>;
  companyId: number;
  corporationId?: number;
  token: string;
  setNotification: TSetNotification;
  onErrorResolve?: () => void;
  selectedDirectorIds?: number[];
  setSelectedDirectorIds?: React.Dispatch<React.SetStateAction<number[]>>;
  modalConfig?: {
    heading?: string;
    isDirector?: boolean;
    isAuthorizedSignatory?: boolean;
    postSave?: (director: {
      id: number;
      directorFirstName: string;
      directorLastName: string;
    }) => void;
  };
  workRequestData?: { id: string };
}) => {
  // We only show masked SSN in the input field
  // To enforce valid SSNs, we check if the input is dirty with this line
  // isDirty would be preferred but there was a race condition
  const maskedSSNToPopulateInput = !director?.hasNoSSN
    ? director?.ssnMask?.padStart(9, '-')
    : '';

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    clearErrors,
  } = useForm<ResponsiblePartyInfoFormFields>({
    resolver: zodResolver(responsiblePartyInfoSchema),
    defaultValues: {
      directorFirstName: director?.directorFirstName || '',
      directorLastName: director?.directorLastName || '',
      hasSSNorITIN: director?.hasNoSSN ? false : true,
      SSNOrITIN: !director?.hasNoSSN ? director?.ssnMask?.padStart(9, '-') : '',
      __ssnInitialMasked: maskedSSNToPopulateInput,
    },
  });

  const onSubmit = async (formResponses: ResponsiblePartyInfoFormFields) => {
    setIsAddingDirector(true);

    const rawSSN = formResponses.SSNOrITIN ?? '';
    const normalized = normalizeSSNOrITIN(rawSSN);
    const workRequestId = workRequestData?.id;

    const payload: {
      directorFirstName: string;
      directorLastName: string;
      ssn: string | null;
      hasNoSSN: boolean;
      companyId: number;
      corporationHasDirector: {
        corporationId: number;
        directorId: number;
        additionalDirectorInformation: {
          isResponsibleParty: boolean;
          isDirector: boolean;
        };
      }[];
    } = {
      directorFirstName: formResponses.directorFirstName,
      directorLastName: formResponses.directorLastName,
      ssn:
        formResponses.hasSSNorITIN && /^\d{9}$/.test(normalized)
          ? normalized
          : null,
      hasNoSSN: !formResponses.hasSSNorITIN,
      companyId,
      corporationHasDirector: [
        {
          corporationId,
          directorId: director?.id,
          additionalDirectorInformation: {
            isResponsibleParty: true,
            isDirector: director?.corporationHasDirector?.find(
              (chd) => chd.corporationId === corporationId
            )?.additionalDirectorInformation?.isDirector,
          },
        },
      ],
      ...(workRequestId != null ? { wrkReqId: workRequestId } : {}),
    };

    const res = await fetcher(
      director?.id ? `director/${director.id}` : 'director',
      {
        method: 'POST',
        body: payload,
      },
      token
    );

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again later or contact team Commenda if you need help',
      });

      await companyDataMutate();

      setIsAddingDirector(false);

      return;
    }

    if (res.status === 409) {
      const error = await res.json();
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: error.message,
      });

      setIsAddingDirector(false);

      return;
    }

    await companyDataMutate();

    const resData = await res.json();

    if (setSelectedDirectorIds) {
      // Clear previous selections and only add the new director since there can only be one
      setSelectedDirectorIds((prev) => [resData.id]);
    }

    if (onErrorResolve) onErrorResolve();

    if (modalConfig?.postSave) {
      modalConfig.postSave({
        id: resData.id,
        directorFirstName: resData.directorFirstName,
        directorLastName: resData.directorLastName,
      });
    }

    reset();

    setIsAddingDirector(false);

    setOpen(false);

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Responsible party added successfully',
    });
  };

  return (
    <form
      className="flex flex-col max-w-[574px]"
      onSubmit={handleSubmit(onSubmit, (err) => {
        console.error(err);
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message: 'Please enter all the required details.',
        });
      })}
    >
      <DialogHeader>
        <DialogTitle>
          {director
            ? 'Edit responsible party'
            : modalConfig?.heading || 'Add a new responsible party'}
        </DialogTitle>
      </DialogHeader>
      <div className="grow shrink-0 flex flex-col gap-4 overflow-y-auto max-h-[calc(85vh-172px)] py-3 px-6">
        <div className="flex flex-col gap-4">
          <FieldWrapper>
            <Label className="text-sm gap-0.5 text-neutral-1">
              <span>Legal Name (as on your passport)</span>
              <Required />
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="First name"
                {...register('directorFirstName')}
                error={errors?.directorFirstName?.message}
                defaultValue={watch('directorFirstName')}
              />
              <Input
                placeholder="Last name"
                {...register('directorLastName')}
                error={errors?.directorLastName?.message}
                defaultValue={watch('directorLastName')}
              />
            </div>
          </FieldWrapper>
        </div>

        <FieldWrapper id="hasSSINorTIN">
          <Label className="text-sm gap-0.5 text-neutral-1">
            <span>
              If the responsible party has a SSN or ITIN, we can apply online
              and get your EIN within 1 business day. Otherwise, we will need to
              apply by fax, which can take 3-6 weeks. <Required />
            </span>
          </Label>
          <div className="flex flex-col gap-2">
            <RadioGroup
              defaultValue={
                watch('hasSSNorITIN') ? OptionType.YES : OptionType.NO
              }
              className="flex flex-col gap-4"
              onValueChange={(value) => {
                setValue('hasSSNorITIN', value === OptionType.YES);
                if (value === OptionType.NO) {
                  setValue('SSNOrITIN', '');
                  clearErrors('SSNOrITIN');
                }
              }}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  size="lg"
                  value={OptionType.YES}
                  id="has-ssin-yes"
                />
                <label htmlFor="has-ssin-yes">I have a SSN or ITIN</label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  size="lg"
                  value={OptionType.NO}
                  id="has-ssin-no"
                />
                <label htmlFor="has-ssin-no">I don't have a SSN or ITIN</label>
              </div>
            </RadioGroup>
          </div>
          {errors?.hasSSNorITIN && (
            <InputErrorMessage message={errors?.hasSSNorITIN?.message} />
          )}
        </FieldWrapper>

        {watch('hasSSNorITIN') && (
          <FieldWrapper>
            <Label className="text-sm gap-0.5 text-neutral-1">
              <span>
                Social Security Number (SSN) or Individual Tax Identification
                Number (ITIN)
                <Required />
              </span>
            </Label>
            <Input
              placeholder="Enter SSN or ITIN"
              {...register('SSNOrITIN')}
              error={errors?.SSNOrITIN?.message}
              defaultValue={watch('SSNOrITIN')}
              type="password"
            />
          </FieldWrapper>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => setOpen(false)}
        >
          <span>Cancel</span>
        </Button>
        <Button type="submit" variant="primary" disabled={isAddingDirector}>
          {isAddingDirector ? <LoadingIndicator size={32} /> : null}
          <span
            className={classNames(
              'flex items-center',
              isAddingDirector ? 'ml-2' : ''
            )}
          >
            Save
          </span>
        </Button>
      </DialogFooter>
    </form>
  );
};

export { USResponsiblePartyForm };
